import { faCamera } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Box from '@material-ui/core/Box';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import isEmpty from 'lodash/isEmpty';
import { Product } from '@spike/product-model';
import { FunctionComponent } from 'react';
import { Img } from 'react-image';
import { reduceResolution, wbp } from 'Theme';
import Area from '../Area';
import ResumeSection, { BodyResumeSection } from '../ResumeSection';

interface Props {
    product: Product;
    onEdit: () => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            alignItems: 'flex-start',
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            [theme.breakpoints.down(768)]: {
                flexDirection: 'column',
                gap: '16px'
            }
        },
        leftContainer: {
            width: '25%',
            flexShrink: 0,
            [theme.breakpoints.down(768)]: {
                width: '100%'
            }
        },
        rightContainer: {
            width: '70%',
            minWidth: 0,
            [theme.breakpoints.down(768)]: {
                width: '100%'
            }
        },
        imageBox: {
            overflow: 'hidden',
            [theme.breakpoints.down(768)]: {
                width: '96px',
                height: '96px',
                borderRadius: '8px'
            },
            [theme.breakpoints.between(768, 'md')]: {
                width: '147px',
                height: '147px',
                borderRadius: '8px'
            },
            [theme.breakpoints.up('lg')]: {
                width: '184px',
                height: '184px',
                borderRadius: '10px'
            }
        },
        uploadBox: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            backgroundColor: '#E9F0ED',
            [theme.breakpoints.down('md')]: {
                borderRadius: '8px'
            },
            [theme.breakpoints.up('lg')]: {
                borderRadius: '10px'
            }
        },
        uploadIcon: {
            color: '#5E8677',
            [theme.breakpoints.down(768)]: {
                fontSize: '28px',
                lineHeight: '24px'
            },
            [theme.breakpoints.between(768, 'md')]: {
                fontSize: '39px',
                lineHeight: '24px'
            },
            [theme.breakpoints.up('lg')]: {
                fontSize: '49px',
                lineHeight: '30px'
            }
        },
        itemContainer: {
            display: 'flex',
            [theme.breakpoints.down('md')]: {
                marginTop: '8px'
            },
            [theme.breakpoints.up('lg')]: {
                marginTop: '10px'
            }
        },
        rightDataContainer: {
            display: 'flex',
            flexWrap: 'wrap',
            width: '100%',
            [theme.breakpoints.down(768)]: {
                flexDirection: 'column',
                gap: '12px',
                marginBottom: '12px'
            },
            [theme.breakpoints.down(wbp)]: {
                marginBottom: `${reduceResolution(27)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                marginBottom: '27px'
            }
        },
        item: {
            minWidth: '250px',
            [theme.breakpoints.down(768)]: {
                minWidth: 0,
                width: '100%',
                marginRight: 0
            },
            [theme.breakpoints.down(wbp)]: {
                marginRight: `${reduceResolution(35)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                marginRight: '35px'
            }
        }
    })
);

export const ResumeDetails: FunctionComponent<Props> = props => {
    const classes = useStyles();

    return (
        <ResumeSection
            title="Product Details"
            {...props}
        >
            <BodyResumeSection>
                <Box className={classes.container}>
                    <Box className={classes.leftContainer}>
                        <Box className={classes.imageBox}>
                            {props.product.businessProduct?.imageUrl ? (
                                <Img
                                    src={props.product.businessProduct?.imageUrl}
                                    className={classes.imageBox}
                                />
                            ) : (
                                <Box className={classes.uploadBox}>
                                    <FontAwesomeIcon
                                        icon={faCamera}
                                        className={classes.uploadIcon}
                                    />
                                </Box>
                            )}
                        </Box>
                    </Box>

                    <Box className={classes.rightContainer}>
                        <Box className={classes.rightDataContainer}>
                            <Area
                                title="PRODUCT"
                                info={
                                    isEmpty(props.product.businessProduct?.name)
                                        ? props.product.name
                                        : props.product.businessProduct?.name
                                }
                                subInfo={props.product.businessProduct?.description}
                                description={props.product.businessProduct?.code || '-'}
                            />
                        </Box>
                        <Box className={classes.rightDataContainer}>
                            {props.product.manufacturer && (
                                <Box className={classes.item}>
                                    <Area
                                        title="BRAND"
                                        info={
                                            isEmpty(props.product.businessProduct?.brand)
                                                ? props.product.brand
                                                : props.product.businessProduct?.brand
                                        }
                                    />
                                </Box>
                            )}
                            {props.product.category && (
                                <Box className={classes.item}>
                                    <Area
                                        title="CATEGORY"
                                        info={props.product.category.name}
                                    />
                                </Box>
                            )}

                            {props.product.manufacturer && (
                                <Box className={classes.item}>
                                    <Area
                                        title="MANUFACTURER"
                                        info={
                                            isEmpty(props.product.businessProduct?.manufacturer)
                                                ? props.product.manufacturer
                                                : props.product.businessProduct?.manufacturer
                                        }
                                    />
                                </Box>
                            )}
                        </Box>
                        <Box className={classes.rightDataContainer}>
                            {props.product.size?.unit && (
                                <Box className={classes.itemContainer}>
                                    <Area
                                        title="UNIT MEASUREMENT"
                                        info={
                                            props.product.businessProduct?.size &&
                                            props.product.businessProduct?.size.unit
                                                ? props.product.businessProduct?.size.unit.name
                                                : 'Whole Product'
                                        }
                                    />
                                </Box>
                            )}
                        </Box>
                        <Area
                            title="PRODUCT DESCRIPTION"
                            info={props.product.businessProduct?.description}
                        />
                    </Box>
                </Box>
            </BodyResumeSection>
        </ResumeSection>
    );
};

export default ResumeDetails;
